import type { AxiosError, AxiosResponse } from "axios";
import type {
  GetInvoicePaymentLinkInput,
  InvoicePaymentLinkData,
  InvoicePaymentLinkResponse,
} from "shared";
import { axiosClient } from "utilz";
import { apiErrorHandler } from "utilz/helpers";

const BASE_URL = import.meta.env.VITE_APP_BILLING_BASE_URL;

const PAYMENT_LINK_PATH = "/v1/billing/invoice/payment-link/public";

/**
 * Fetch the latest payment link for an invoice.
 *
 * `GET /v1/billing/invoice/payment-link/public?msp_id=&invoice_id=` — deliberately
 * public: no headers, no session, no API key. `invoice_id` is the invoice's
 * `mapping_id`. Amount, currency and description are computed server-side from the
 * invoice row and are never accepted from the client.
 *
 * Called from the unauthenticated `/pay-now` landing route, so it must never
 * depend on an access token. `axiosClient` only carries an auth interceptor
 * while `SecureWrapper` is mounted, which it is not on public routes.
 */
export const getInvoicePaymentLink = async ({
  mspId,
  invoiceId,
}: GetInvoicePaymentLinkInput): Promise<InvoicePaymentLinkResponse> => {
  try {
    const response = await axiosClient.get(`${BASE_URL}${PAYMENT_LINK_PATH}`, {
      params: { msp_id: mspId, invoice_id: invoiceId },
    });
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      const data: InvoicePaymentLinkData = resData?.data ?? {};
      return { url: data.payment_url ?? "" };
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};
